import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Spinner } from '../../components/Spinner'

interface Episode {
  id: number
  influencer_id: number
  influencer_name: string
  title: string
  youtube_url: string | null
  published_at: string | null
  transcript_source: string | null
  has_transcript: boolean
  prediction_count: number | string
  claim_count: number | string
}

interface Influencer {
  id: number
  name: string
}

export default function AllEpisodes() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialInfluencer = searchParams.get('influencer') || ''

  const [episodes, setEpisodes] = useState<Episode[]>([])
  const [influencers, setInfluencers] = useState<Influencer[]>([])
  const [influencerFilter, setInfluencerFilter] = useState(initialInfluencer)
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [transcripts, setTranscripts] = useState<Record<number, string | null>>({})
  const [loadingTranscript, setLoadingTranscript] = useState<number | null>(null)

  useEffect(() => {
    fetch('/api/intel/influencers')
      .then(r => r.ok ? r.json() as Promise<Influencer[]> : Promise.resolve([]))
      .then(setInfluencers)
      .catch(() => {})
  }, [])

  const fetchEpisodes = useCallback((infId: string) => {
    setLoading(true)
    const params = new URLSearchParams()
    if (infId) params.set('influencer_id', infId)
    fetch(`/api/intel/episodes?${params}`)
      .then(r => r.ok ? r.json() as Promise<Episode[]> : Promise.resolve([]))
      .then(data => { setEpisodes(data); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  useEffect(() => {
    fetchEpisodes(influencerFilter)
  }, [influencerFilter, fetchEpisodes])

  const handleInfluencerChange = (value: string) => {
    setInfluencerFilter(value)
    if (value) {
      setSearchParams({ influencer: value })
    } else {
      setSearchParams({})
    }
    setExpandedId(null)
  }

  const toggleTranscript = async (ep: Episode) => {
    if (expandedId === ep.id) {
      setExpandedId(null)
      return
    }
    setExpandedId(ep.id)
    if (transcripts[ep.id] !== undefined) return
    if (!ep.has_transcript) {
      setTranscripts(prev => ({ ...prev, [ep.id]: null }))
      return
    }
    setLoadingTranscript(ep.id)
    try {
      const res = await fetch(`/api/intel/episodes/${ep.id}`)
      const data = await res.json() as { transcript?: string }
      setTranscripts(prev => ({ ...prev, [ep.id]: data.transcript || null }))
    } catch {
      setTranscripts(prev => ({ ...prev, [ep.id]: null }))
    } finally {
      setLoadingTranscript(null)
    }
  }

  return (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold mb-4 text-gray-900">Episodes & Transcripts</h2>

      <div className="flex flex-wrap gap-2 mb-4">
        <select
          value={influencerFilter}
          onChange={e => handleInfluencerChange(e.target.value)}
          className="w-full sm:w-auto bg-white border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-900"
        >
          <option value="">All KOLs</option>
          {influencers.map(i => (
            <option key={i.id} value={String(i.id)}>{i.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-lg h-[400px] flex flex-col items-center justify-center gap-3">
          <Spinner size={32} />
          <span className="text-sm text-gray-500">Loading episodes...</span>
        </div>
      ) : episodes.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
          No episodes found.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-3 font-semibold text-gray-900">KOL</th>
                <th className="text-left py-3 px-3 font-semibold text-gray-900">Title</th>
                <th className="text-left py-3 px-3 font-semibold text-gray-900 hidden md:table-cell">Published</th>
                <th className="text-center py-3 px-3 font-semibold text-gray-900">Transcript</th>
                <th className="text-center py-3 px-3 font-semibold text-gray-900 hidden sm:table-cell">Predictions</th>
                <th className="text-center py-3 px-3 font-semibold text-gray-900 hidden sm:table-cell">Claims</th>
                <th className="text-center py-3 px-3 font-semibold text-gray-900">YouTube</th>
              </tr>
            </thead>
            <tbody>
              {episodes.map(ep => (
                <>
                  <tr
                    key={ep.id}
                    className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleTranscript(ep)}
                  >
                    <td className="py-3 px-3 font-medium text-gray-900 whitespace-nowrap">{ep.influencer_name}</td>
                    <td className="py-3 px-3 text-gray-700">
                      <span className="line-clamp-2">{ep.title || '(untitled)'}</span>
                    </td>
                    <td className="py-3 px-3 text-gray-500 hidden md:table-cell whitespace-nowrap">
                      {ep.published_at ? ep.published_at.slice(0, 10) : '—'}
                    </td>
                    <td className="py-3 px-3 text-center">
                      {ep.has_transcript ? (
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                          {ep.transcript_source || 'yes'}
                        </span>
                      ) : (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                          none
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center text-gray-700 hidden sm:table-cell">
                      {Number(ep.prediction_count)}
                    </td>
                    <td className="py-3 px-3 text-center text-gray-700 hidden sm:table-cell">
                      {Number(ep.claim_count)}
                    </td>
                    <td className="py-3 px-3 text-center" onClick={e => e.stopPropagation()}>
                      {ep.youtube_url ? (
                        <a
                          href={ep.youtube_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          Watch
                        </a>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                  {expandedId === ep.id && (
                    <tr key={`${ep.id}-transcript`} className="border-b border-gray-200 bg-gray-50">
                      <td colSpan={7} className="px-4 py-4">
                        {loadingTranscript === ep.id ? (
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Spinner size={16} />
                            Loading transcript...
                          </div>
                        ) : transcripts[ep.id] ? (
                          <div className="max-h-[60vh] overflow-y-auto">
                            <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed font-mono">
                              {transcripts[ep.id]}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic">No transcript available.</p>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-400">
        {!loading && `${episodes.length} episode${episodes.length !== 1 ? 's' : ''}`}
        {influencerFilter && (
          <>
            {' — '}
            <button
              onClick={() => handleInfluencerChange('')}
              className="text-blue-500 hover:underline"
            >
              Show all KOLs
            </button>
          </>
        )}
      </div>
    </div>
  )
}
